import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { parseISO } from 'date-fns';
import { Contact } from '@/types/Contact';
import { Conversation } from '@/types/Conversation';
import { Booking } from '@/types/Booking';
import { Service } from '@/types/Service';
import { selectAgents } from '@/redux/agentsSlice';
import { bookingsApi, servicesApi } from '@/services/api';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsTrigger } from '@/components/ui/tabs';
import { AnimatedTabsList } from '@/components/ui/animated-tabs-list';
import {
  Phone,
  Clock,
  ExternalLink,
  Pencil,
  Calendar,
  Loader2,
} from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { hasCrm } from '@/utils/crmUtils';
import { useDateFormat } from '@/hooks/useDateFormat';

interface ContactSheetProps {
  contact: Contact | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (contactId: number, name: string) => Promise<void>;
  conversations: Conversation[];
  agentId: number;
  autoEdit?: boolean;
  context?: 'conversation' | 'contacts';
  currentConversationId?: string;
}

const contactActivityTabListClassName =
  'w-full justify-center rounded-full border border-border bg-muted/30 p-1';

const contactActivityTabTriggerClassName =
  'relative z-10 flex flex-1 items-center justify-center gap-1.5 rounded-full bg-transparent px-3 py-2 text-sm font-semibold data-[state=active]:bg-transparent data-[state=active]:text-brand-foreground data-[state=inactive]:text-muted-foreground';

const contactActivityTabContentClassName =
  'mt-0 data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:slide-in-from-bottom-1 data-[state=active]:duration-200';

const getStatusBadgeClass = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'confirmed':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    case 'pending':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    case 'cancelled':
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    default:
      return 'bg-muted text-muted-foreground';
  }
};

const ContactSheet: React.FC<ContactSheetProps> = ({
  contact,
  isOpen,
  onClose,
  onSave,
  conversations,
  agentId,
  autoEdit = false,
  currentConversationId,
}) => {
  const { t } = useTranslation();
  const df = useDateFormat();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [name, setName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [isLoadingBookings, setIsLoadingBookings] = useState(false);
  const [activityTab, setActivityTab] = useState<
    'conversations' | 'appointments'
  >('conversations');
  const nameInputRef = React.useRef<HTMLInputElement>(null);

  const agents = useSelector(selectAgents);
  const currentAgent = agents.find(agent => agent.id === agentId);
  const crmEnabled = hasCrm(currentAgent);
  const updatedContact = currentAgent?.contacts?.find(
    c => c.id === contact?.id
  );

  const displayContact = updatedContact || contact;

  React.useEffect(() => {
    if (displayContact) {
      setName(displayContact.name || '');
      if (autoEdit) {
        setIsEditing(true);
        setTimeout(() => {
          if (nameInputRef.current) {
            nameInputRef.current.focus();
            nameInputRef.current.select();
          }
        }, 100);
      } else {
        setIsEditing(false);
      }
    }
  }, [displayContact, autoEdit]);

  React.useEffect(() => {
    if (!isOpen) {
      setActivityTab('conversations');
      return;
    }

    if (!crmEnabled || !displayContact?.id) {
      setBookings([]);
      setServices([]);
      setIsLoadingBookings(false);
      return;
    }

    let cancelled = false;
    setIsLoadingBookings(true);

    void (async () => {
      try {
        const [bookingsData, servicesData] = await Promise.all([
          bookingsApi.fetchBookingsByContactId(agentId, displayContact.id),
          servicesApi.fetchServices(agentId),
        ]);
        if (!cancelled) {
          setBookings(bookingsData);
          setServices(servicesData);
        }
      } catch (error) {
        console.error('Failed to load contact appointments:', error);
        if (!cancelled) setBookings([]);
      } finally {
        if (!cancelled) setIsLoadingBookings(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, displayContact?.id, agentId, crmEnabled]);

  const handleSave = async () => {
    if (!displayContact || !name.trim()) return;

    try {
      setIsSaving(true);
      await onSave(displayContact.id, name.trim());
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save contact:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setName(displayContact?.name || '');
    setIsEditing(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
    setTimeout(() => {
      nameInputRef.current?.focus();
      nameInputRef.current?.select();
    }, 0);
  };

  const handleConversationClick = (conversationId: string) => {
    if (isNavigating) return;

    setIsNavigating(true);
    onClose();
    navigate(`/agents/${agentId}/conversation/${conversationId}`);
  };

  const handleAppointmentClick = (bookingId: number) => {
    if (isNavigating) return;

    setIsNavigating(true);
    onClose();
    navigate(`/agents/${agentId}?tab=calendar&booking=${bookingId}`);
  };

  const contactConversations = conversations.filter(
    conv => conv.caller_id === displayContact?.caller_id
  );

  const sortedConversations = [...contactConversations].sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  const now = new Date();
  const upcomingBookings = bookings
    .filter(b => parseISO(b.start_time) >= now)
    .sort(
      (a, b) =>
        parseISO(a.start_time).getTime() - parseISO(b.start_time).getTime()
    );
  const pastBookings = bookings
    .filter(b => parseISO(b.start_time) < now)
    .sort(
      (a, b) =>
        parseISO(b.start_time).getTime() - parseISO(a.start_time).getTime()
    );

  const getServiceName = (serviceId: number | null): string => {
    if (!serviceId) {
      return t(
        'agent.calendar.appointment.no_service.translation',
        'No Service'
      );
    }
    return (
      services.find(s => s.id === serviceId)?.name ||
      t('agent.calendar.appointment.no_service.translation', 'No Service')
    );
  };

  const formatDuration = (seconds: number | null): string => {
    if (seconds === null) return '0s';
    if (seconds < 60) return `${seconds}s`;

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m${remainingSeconds}s`;
  };

  const renderConversationList = () => {
    if (sortedConversations.length === 0) {
      return (
        <div className="py-8 text-center text-muted-foreground">
          {conversations.length === 0 ? (
            <div>
              <p className="mb-2 text-sm text-muted-foreground">
                {t('contacts.no_conversations_data.translation')}
              </p>
              <p className="text-xs text-muted-foreground">
                {t('contacts.no_conversations_data_description.translation')}
              </p>
            </div>
          ) : (
            <div>
              <p className="mb-2 text-sm text-muted-foreground">
                {t('contacts.no_conversations_found.translation')}
              </p>
              <p className="text-xs text-muted-foreground">
                {t('contacts.conversation_contact_label.translation', {
                  caller_id: displayContact?.caller_id,
                })}
              </p>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {sortedConversations.map(conv => (
          <div
            key={conv.id}
            className={cn(
              'flex items-center justify-between rounded-lg bg-card p-3 transition-all duration-200',
              isNavigating
                ? 'cursor-not-allowed opacity-50'
                : 'group cursor-pointer hover:bg-conversation-light-secondary dark:hover:bg-conversation-dark-secondary',
              currentConversationId &&
                conv.id.toString() === currentConversationId &&
                'bg-blue-50 ring-2 ring-blue-500 dark:bg-blue-950/20'
            )}
            onClick={
              isNavigating
                ? undefined
                : () => handleConversationClick(conv.id.toString())
            }
            title={
              isNavigating
                ? t('contacts.navigating.translation', 'Navigating...')
                : t(
                    'contacts.click_to_view_conversation.translation',
                    'Click to view conversation'
                  )
            }
          >
            <div className="flex items-center space-x-3">
              <Phone className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">
                  {df.date(conv.created_at)}
                  {currentConversationId &&
                    conv.id.toString() === currentConversationId && (
                      <span className="ml-2 text-xs font-medium text-blue-600 dark:text-blue-400">
                        {t('contacts.current_label.translation')}
                      </span>
                    )}
                </p>
                <p className="text-xs text-muted-foreground">
                  {df.time(conv.created_at)}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span>{formatDuration(conv.duration)}</span>
              <ExternalLink className="ml-1 h-3 w-3 text-muted-foreground transition-colors group-hover:text-foreground" />
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderBookingRow = (booking: Booking) => {
    const startTime = parseISO(booking.start_time);
    const endTime = parseISO(booking.end_time);
    const isCancelled = booking.status.toLowerCase() === 'cancelled';

    return (
      <div
        key={booking.id}
        className={cn(
          'flex items-center justify-between rounded-lg bg-card p-3 transition-all duration-200',
          isNavigating
            ? 'cursor-not-allowed opacity-50'
            : 'group cursor-pointer hover:bg-conversation-light-secondary dark:hover:bg-conversation-dark-secondary'
        )}
        onClick={
          isNavigating ? undefined : () => handleAppointmentClick(booking.id)
        }
        title={
          isNavigating
            ? t('contacts.navigating.translation', 'Navigating...')
            : t(
                'contacts.click_to_view_appointment.translation',
                'Click to view appointment'
              )
        }
      >
        <div className="flex min-w-0 items-center space-x-3">
          <Calendar className="h-4 w-4 shrink-0 text-muted-foreground" />
          <div className="min-w-0">
            <p
              className={cn(
                'truncate text-sm font-medium',
                isCancelled && 'text-muted-foreground line-through'
              )}
            >
              {getServiceName(booking.service_id)}
            </p>
            <p className="text-xs text-muted-foreground">
              {df.dateWithWeekday(startTime)} · {df.time(startTime)} -{' '}
              {df.time(endTime)}
            </p>
          </div>
        </div>
        <div className="ml-2 flex shrink-0 items-center space-x-2">
          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-xs font-medium capitalize',
              getStatusBadgeClass(booking.status)
            )}
          >
            {booking.status}
          </span>
          <ExternalLink className="h-3 w-3 text-muted-foreground transition-colors group-hover:text-foreground" />
        </div>
      </div>
    );
  };

  const renderAppointmentList = () => {
    if (isLoadingBookings) {
      return (
        <div className="flex items-center justify-center py-8 text-muted-foreground">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          <span className="text-sm">
            {t('common.loading.translation', 'Loading...')}
          </span>
        </div>
      );
    }

    if (bookings.length === 0) {
      return (
        <div className="py-8 text-center text-muted-foreground">
          <p className="text-sm">
            {t(
              'contacts.no_appointments.translation',
              'No appointments found for this contact'
            )}
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {upcomingBookings.length > 0 && (
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('contacts.upcoming_appointments.translation', 'Upcoming')}
            </p>
            {upcomingBookings.map(renderBookingRow)}
          </div>
        )}
        {pastBookings.length > 0 && (
          <div className="space-y-3">
            {upcomingBookings.length > 0 && (
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('contacts.past_appointments.translation', 'Past')}
              </p>
            )}
            {pastBookings.map(renderBookingRow)}
          </div>
        )}
      </div>
    );
  };

  const content = (
    <div className="space-y-6">
      <Card className="bg-card">
        <CardHeader>
          <CardTitle>{t('contacts.info_title.translation')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="name" className="text-right">
              {t('contacts.name.translation', 'Name')}
            </Label>
            <div className="col-span-3 flex min-w-0 items-center gap-1">
              <Input
                id="name"
                variant="borderless"
                value={
                  isEditing
                    ? name
                    : displayContact?.name ||
                      t('contacts.unnamed.translation', 'Unnamed')
                }
                onChange={e => setName(e.target.value)}
                className="min-w-0 flex-1 bg-background text-lg font-medium disabled:bg-transparent disabled:text-foreground"
                placeholder={t(
                  'contacts.enter_name.translation',
                  'Enter contact name'
                )}
                ref={nameInputRef}
                disabled={!isEditing}
              />
              {!isEditing && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={handleEdit}
                  className="h-8 w-8 shrink-0 bg-transparent hover:bg-transparent"
                  aria-label={t('common.edit.translation', 'Edit')}
                >
                  <Pencil className="h-4 w-4 text-brand" />
                </Button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label className="text-right text-base text-muted-foreground">
              {t('contacts.phone_number.translation', 'Phone')}
            </Label>
            <span className="col-span-3 text-foreground">
              {displayContact?.caller_id}
            </span>
          </div>
          {isEditing && (
            <div className="flex justify-end space-x-2 pt-2">
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={isSaving}
              >
                {t('common.cancel.translation', 'Cancel')}
              </Button>
              <Button
                variant="brand"
                onClick={handleSave}
                disabled={isSaving || !name.trim()}
              >
                {isSaving
                  ? t('common.saving.translation', 'Saving...')
                  : t('common.save.translation', 'Save')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-card">
        {crmEnabled ? (
          <Tabs
            value={activityTab}
            onValueChange={value =>
              setActivityTab(value as 'conversations' | 'appointments')
            }
          >
            <CardHeader className="pb-3">
              <AnimatedTabsList
                activeValue={activityTab}
                indicatorClassName="bg-brand"
                className={contactActivityTabListClassName}
              >
                <TabsTrigger
                  value="conversations"
                  className={contactActivityTabTriggerClassName}
                >
                  {t('contacts.conversations.translation', 'Conversations')}
                  <span className="text-sm font-normal opacity-80">
                    ({contactConversations.length})
                  </span>
                </TabsTrigger>
                <TabsTrigger
                  value="appointments"
                  className={contactActivityTabTriggerClassName}
                >
                  {t('contacts.appointments.translation', 'Appointments')}
                  <span className="text-sm font-normal opacity-80">
                    ({isLoadingBookings ? '…' : bookings.length})
                  </span>
                </TabsTrigger>
              </AnimatedTabsList>
            </CardHeader>
            <CardContent>
              <TabsContent
                value="conversations"
                className={contactActivityTabContentClassName}
              >
                {renderConversationList()}
              </TabsContent>
              <TabsContent
                value="appointments"
                className={contactActivityTabContentClassName}
              >
                {renderAppointmentList()}
              </TabsContent>
            </CardContent>
          </Tabs>
        ) : (
          <>
            <CardHeader>
              <CardTitle>
                {t('contacts.conversations.translation', 'Conversations')}
              </CardTitle>
            </CardHeader>
            <CardContent>{renderConversationList()}</CardContent>
          </>
        )}
      </Card>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={onClose}>
        <DrawerContent className="flex max-h-[90vh] flex-col">
          <DrawerHeader className="flex-shrink-0 pb-6">
            <DrawerTitle className="text-lg">
              <div className="flex flex-row items-center gap-1">
                <span>{t('contacts.header_prefix.translation')}</span>
                <span className="truncate">{currentAgent?.name}</span>
              </div>
            </DrawerTitle>
            <DrawerDescription className="sr-only">
              {t('contacts.header_description.translation')}
            </DrawerDescription>
          </DrawerHeader>
          <div className="flex-1 overflow-y-auto px-4 pb-4">{content}</div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="overflow-y-auto">
        <SheetHeader className="pb-6">
          <SheetTitle className="text-lg">
            <div className="flex flex-row items-center gap-1">
              <span>{t('contacts.header_prefix.translation')}</span>
              <span className="truncate">{currentAgent?.name}</span>
            </div>
          </SheetTitle>
          <SheetDescription className="sr-only">
            {t('contacts.header_description.translation')}
          </SheetDescription>
        </SheetHeader>
        {content}
      </SheetContent>
    </Sheet>
  );
};

export default ContactSheet;
